<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue';
import BaseInput from '../ui/inputs/BaseInput.vue';
import Select from '../ui/select/Select.vue';
import Price from '../ui/inputs/Price.vue';

import type { AddAdditionalRequestInput, AdditionalRequest } from '@/interfaces';
import FileInput from '../files/FileInput.vue';
import { ScaleIcon, CurrencyEuroIcon } from '@heroicons/vue/20/solid';
import BaseButton from '@/components/ui/buttons/BaseButton.vue';
import adminAPI from '@/services/api';
import { useNotificationStore } from '@/store';
import { LoadedFile } from '../files/types';
import { AdditionalRequestsTypesMap } from '@/utils/config/constants';
import Dinero from 'dinero.js';
import { computedAsync } from '@vueuse/core';

const emits = defineEmits(["upserted"]);

const props = defineProps<{
  claimId: string;
  selectedRequest: AdditionalRequest | null;
}>();


const additionalRequestsTypeOptions = Object.values(AdditionalRequestsTypesMap);

const defaultRequest: AddAdditionalRequestInput = {
  reason: props.selectedRequest?.reason || "",
  amountRequested: props.selectedRequest?.amountRequested || 0,
  amountReceived: props.selectedRequest?.amountReceived || 0,
  additionalRequestType: props.selectedRequest?.additionalRequestTypeName || "ARTICLE_700"
};
const additionalRequest = ref<AddAdditionalRequestInput>(defaultRequest);


const isLoading = ref(false);
async function upsertAdditionalRequest() {
  isLoading.value = true;

  const { success } = await adminAPI.upsertAdditionalRequest(props.claimId, {
    ...additionalRequest.value,
    amountRequested: typeof additionalRequest.value.amountRequested === "object"
      ? (additionalRequest.value.amountRequested as any as Dinero.Dinero).getAmount()
      : additionalRequest.value.amountRequested, // Ugly code, but Pricing is emiting a Dinero object...
    amountReceived: typeof additionalRequest.value.amountReceived === "object"
      ? (additionalRequest.value.amountReceived as any as Dinero.Dinero).getAmount()
      : additionalRequest.value.amountReceived, // Ugly code, but Pricing is emiting a Dinero object...
  });
  isLoading.value = false;
  if (success) {
    emits("upserted");
    useNotificationStore().emitNotification({ title: "Demande ajoutée avec succès" });
    additionalRequest.value = defaultRequest;
  }
}
</script>
<template>
<div class="p-6 m-2 border rounded-lg shadow">
  <form class="text-left">
    <div class="mb-6 divide-y-8 divide-transparent">
      <Select
        :disabled="Boolean(props.selectedRequest?.additionalRequestType)"
        :selected-option="additionalRequestsTypeOptions.findIndex(o => o.value === defaultRequest.additionalRequestType)"
        @update:modelValue="selected => additionalRequest.additionalRequestType = selected.value"
        label="Type de demande" :options="additionalRequestsTypeOptions"
        select-check-position="right"
      />
      <Price label="Montant demandé" :default="defaultRequest.amountRequested / 100" placeholder="128.52" :icon="CurrencyEuroIcon" v-model="additionalRequest.amountRequested" />
      <Price label="Montant obtenu" :default="(defaultRequest.amountReceived as number) / 100" placeholder="0" :icon="CurrencyEuroIcon" v-model="additionalRequest.amountReceived" />
      <BaseInput label="Raison" :value="defaultRequest.reason" placeholder="Absence de réponse de la compagnie depuis 2 mois" v-model="additionalRequest.reason" />
    </div>
    <div class="flex flex-row-reverse">
      <BaseButton text="Enregistrer la demande" size="lg" @click="upsertAdditionalRequest()" :icon="ScaleIcon" :loading="isLoading" />
    </div>
  </form>
</div>
</template>